import type { Entity } from '@backstage/catalog-model';
import { getAnnotation } from './annotations';
import { normalizeEntityRef } from './connections';

export const ANNOTATION_C4_COMPONENTS = 'aurum.com.br/c4-components';

export interface C4ComponentTarget {
  raw: string;
  entityRef?: string;
}

export interface C4ComponentDefinition {
  name: string;
  technology?: string;
  description?: string;
  targets: C4ComponentTarget[];
}

const FIELD_SEPARATOR = '|';
const MAX_FIELDS = 4;
const TARGET_SEPARATOR = ',';

function parseTarget(raw: string): C4ComponentTarget | undefined {
  const trimmed = raw.trim();
  if (!trimmed) {
    return undefined;
  }

  if (!trimmed.includes(':')) {
    return { raw: trimmed };
  }

  const entityRef = normalizeEntityRef(trimmed);
  return entityRef ? { raw: trimmed, entityRef } : { raw: trimmed };
}

function parseLine(line: string): C4ComponentDefinition | undefined {
  const fields = line.split(FIELD_SEPARATOR, MAX_FIELDS).map(f => f.trim());
  const name = fields[0];
  if (!name) {
    return undefined;
  }

  const targets = (fields[3] ?? '')
    .split(TARGET_SEPARATOR)
    .map(parseTarget)
    .filter((target): target is C4ComponentTarget => Boolean(target));

  return {
    name,
    ...(fields[1] ? { technology: fields[1] } : {}),
    ...(fields[2] ? { description: fields[2] } : {}),
    targets,
  };
}

export function parseC4Components(
  entity: Entity | undefined,
): C4ComponentDefinition[] {
  const raw = entity
    ? getAnnotation(entity, ANNOTATION_C4_COMPONENTS)
    : undefined;
  if (!raw) {
    return [];
  }

  return raw
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0 && !line.startsWith('#'))
    .map(parseLine)
    .filter((component): component is C4ComponentDefinition =>
      Boolean(component),
    );
}

export function isInternalTarget(
  target: C4ComponentTarget,
  components: C4ComponentDefinition[],
): boolean {
  if (target.entityRef) {
    return false;
  }
  const name = target.raw.toLocaleLowerCase('en-US');
  return components.some(c => c.name.toLocaleLowerCase('en-US') === name);
}

export function collectExternalRefs(
  components: C4ComponentDefinition[],
): string[] {
  const refs: string[] = [];
  for (const component of components) {
    for (const target of component.targets) {
      if (target.entityRef && !refs.includes(target.entityRef)) {
        refs.push(target.entityRef);
      }
    }
  }
  return refs;
}
